import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineConfirm from '../../src/components/InlineConfirm';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive, rs } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface LocationDetail { locationId: string; name: string; role: string; }
interface AttendantItem {
  id: string; name: string; email: string; phone: string | null; accountNumber: string | null;
  isActive: boolean;
  businessName: string; locations: string[]; locationDetails: LocationDetail[];
  ownerId: string; createdAt: string;
}

/** AdminAttendants - list, search, and manage attendants across all locations and owners. */
export default function AdminAttendants() {
  const { colors } = useTheme();
  const { width, height } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const { ownerId: paramOwnerId } = useLocalSearchParams<{ ownerId?: string }>();
  const router = useRouter();
  const [attendantsList, setAttendantsList] = useState<AttendantItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editItem, setEditItem] = useState<AttendantItem | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [addLocId, setAddLocId] = useState('');
  const [addLocRole, setAddLocRole] = useState('junior');
  const [ownerLocations, setOwnerLocations] = useState<{ id: string; name: string }[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async (p = 1, s = '', ownerFilter = paramOwnerId, signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (ownerFilter) params.set('ownerId', ownerFilter);
      const data = await api<{ attendants: AttendantItem[]; total: number }>(`/api/admin/attendants?${params}`);
      if (signal?.aborted) return;
      setAttendantsList(data.attendants);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramOwnerId]);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', paramOwnerId, ac.signal); return () => ac.abort(); }, [paramOwnerId]);

  const openEdit = async (item: AttendantItem) => {
    setEditItem(item);
    setEditData({ name: item.name, email: item.email, isActive: item.isActive });
    setAddLocId('');
    setAddLocRole('junior');
    setErrorMsg('');
    setShowDeleteConfirm(false);
    // Fetch owner's locations for add-location picker
    try {
      const data = await api<{ locations: { id: string; name: string }[] }>(`/api/admin/locations?ownerId=${item.ownerId}&limit=100`);
      setOwnerLocations(data.locations);
    } catch { setOwnerLocations([]); }
  };

  const handleSave = async () => {
    if (!editItem || !editData) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await api(`/api/admin/attendants/${editItem.id}`, { method: 'PUT', body: editData });
      setEditItem(null);
      fetchData(page, search);
    } catch (err: any) { setErrorMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteAttendant = () => {
    if (!editItem) return;
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAttendant = async () => {
    if (!editItem) return;
    setDeleting(true);
    setErrorMsg('');
    try {
      await api(`/api/admin/attendants/${editItem.id}`, { method: 'DELETE' });
      setShowDeleteConfirm(false);
      setEditItem(null);
      fetchData(page, search);
    } catch (err: any) {
      setErrorMsg(err.message);
      setShowDeleteConfirm(false);
    } finally { setDeleting(false); }
  };

  const refreshAttendant = async (id: string) => {
    try {
      const updated = await api<AttendantItem>(`/api/admin/attendants/${id}`);
      setEditItem(updated);
    } catch (err: any) { console.error('Failed to refresh attendant:', err.message); }
  };

  const handleAddLocation = async () => {
    if (!editItem || !addLocId) return;
    setErrorMsg('');
    try {
      await api(`/api/admin/attendants/${editItem.id}/locations`, { method: 'POST', body: { locationId: addLocId, role: addLocRole } });
      await refreshAttendant(editItem.id);
      setAddLocId('');
    } catch (err: any) { setErrorMsg(err.message); }
  };

  const handleUpdateLocRole = async (locationId: string, role: string) => {
    if (!editItem) return;
    setErrorMsg('');
    try {
      await api(`/api/admin/attendants/${editItem.id}/locations/${locationId}`, { method: 'PATCH', body: { role } });
      await refreshAttendant(editItem.id);
    } catch (err: any) { setErrorMsg(err.message); }
  };

  const handleRemoveLocation = async (locationId: string) => {
    if (!editItem) return;
    setErrorMsg('');
    try {
      await api(`/api/admin/attendants/${editItem.id}/locations/${locationId}`, { method: 'DELETE' });
      await refreshAttendant(editItem.id);
    } catch (err: any) { setErrorMsg(err.message); }
  };

  const csvEndpoint = `/api/admin/attendants?${new URLSearchParams({ ...(search && { search }) })}`;

  const renderItem = ({ item }: { item: AttendantItem }) => (
    <View style={styles.row}>
      <View style={styles.avatar}>
        <Ionicons name="person" size={18} color={colors.primary} />
      </View>
      <TouchableOpacity style={styles.rowBody} onPress={() => router.push(`/admin/shifts?attendantId=${item.id}` as never)}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{item.name}</Text>
          <View style={[styles.badge, { backgroundColor: item.isActive ? '#05966915' : '#dc262615' }]}>
            <Text style={[styles.badgeText, { color: item.isActive ? '#059669' : '#dc2626' }]}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.email}
          {item.accountNumber ? ` · ${item.accountNumber}` : ''}
          {item.phone ? ` · ${item.phone}` : ''}
        </Text>
        <Text style={styles.sub}>Owner: {item.businessName} · Locations: {item.locations.join(', ') || 'None'}</Text>
      </TouchableOpacity>
      <TouchableOpacity onPress={() => openEdit(item)} hitSlop={8}>
        <Ionicons name="create-outline" size={18} color={colors.primary} />
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search)}
        csvEndpoint={csvEndpoint} csvFilename="attendants.csv" label="attendant"
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={attendantsList} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No attendants found</Text>}
        />
      )}

      <Modal visible={!!editItem} transparent animationType="slide" onRequestClose={() => setEditItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Edit Attendant</Text>
            {editData && editItem && (
              <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 500 }} nestedScrollEnabled>
                <Text style={styles.fieldLabel}>Name</Text>
                <TextInput style={styles.fieldInput} value={editData.name} onChangeText={(v) => setEditData({ ...editData, name: v })} maxLength={32} />
                <Text style={styles.fieldLabel}>Email</Text>
                <TextInput style={styles.fieldInput} value={editData.email} onChangeText={(v) => setEditData({ ...editData, email: v })} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
                <TouchableOpacity style={styles.toggleRow} onPress={() => setEditData({ ...editData, isActive: !editData.isActive })}>
                  <Ionicons name={editData.isActive ? 'checkmark-circle' : 'close-circle'} size={24} color={editData.isActive ? '#059669' : '#dc2626'} />
                  <Text style={styles.toggleText}>{editData.isActive ? 'Active' : 'Inactive'}</Text>
                </TouchableOpacity>

                <Text style={[styles.fieldLabel, { marginTop: spacing.md }]}>Location Assignments</Text>
                {(editItem.locationDetails || []).map((loc) => (
                  <View key={loc.locationId} style={styles.locAssignRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.name}>{loc.name}</Text>
                      <View style={styles.chipRow}>
                        {['junior', 'senior', 'lead'].map(r => (
                          <TouchableOpacity key={r} onPress={() => handleUpdateLocRole(loc.locationId, r)}
                            style={[styles.chipSm, loc.role === r && styles.chipSmActive]}>
                            <Text style={[styles.chipSmText, loc.role === r && { color: colors.white }]}>{r}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                    <TouchableOpacity onPress={() => handleRemoveLocation(loc.locationId)} hitSlop={8}>
                      <Ionicons name="close-circle" size={20} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                ))}
                {(editItem.locationDetails || []).length === 0 && <Text style={styles.empty}>No locations assigned</Text>}

                <Text style={[styles.fieldLabel, { marginTop: spacing.sm }]}>Add Location</Text>
                <View style={styles.addLocRow}>
                  <View style={{ flex: 1 }}>
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 4 }}>
                      {ownerLocations
                        .filter(l => !(editItem.locationDetails || []).find(d => d.locationId === l.id))
                        .map(l => (
                          <TouchableOpacity key={l.id} onPress={() => setAddLocId(l.id)}
                            style={[styles.chipSm, addLocId === l.id && styles.chipSmActive, { marginRight: 4 }]}>
                            <Text style={[styles.chipSmText, addLocId === l.id && { color: colors.white }]}>{l.name}</Text>
                          </TouchableOpacity>
                        ))}
                    </ScrollView>
                    <View style={styles.chipRow}>
                      {['junior', 'senior', 'lead'].map(r => (
                        <TouchableOpacity key={r} onPress={() => setAddLocRole(r)}
                          style={[styles.chipSm, addLocRole === r && styles.chipSmActive]}>
                          <Text style={[styles.chipSmText, addLocRole === r && { color: colors.white }]}>{r}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                  <TouchableOpacity onPress={handleAddLocation} disabled={!addLocId} style={{ opacity: addLocId ? 1 : 0.3 }}>
                    <Ionicons name="add-circle" size={28} color={colors.primary} />
                  </TouchableOpacity>
                </View>

                <TouchableOpacity style={styles.deleteBtn} onPress={handleDeleteAttendant}>
                  <Ionicons name="trash" size={16} color={colors.danger} />
                  <Text style={styles.deleteText}>Delete Attendant</Text>
                </TouchableOpacity>
                <InlineConfirm
                  message={`Delete ${editItem.name}? This cannot be undone.`}
                  visible={showDeleteConfirm}
                  onConfirm={confirmDeleteAttendant}
                  onCancel={() => setShowDeleteConfirm(false)}
                  confirmLabel="Delete"
                  loading={deleting}
                  variant="danger"
                />
              </ScrollView>
            )}
            <InlineMessage type="error" message={errorMsg} visible={!!errorMsg} onDismiss={() => setErrorMsg('')} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setEditItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={handleSave} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 10 },
  avatar: { width: rs(36), height: rs(36), borderRadius: rs(18), backgroundColor: c.primaryBg, justifyContent: 'center', alignItems: 'center' },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 420 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  fieldInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, marginTop: 4, borderWidth: 1, borderColor: c.border },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  toggleText: { fontSize: fontSize.md, color: c.text },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  chipSm: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  chipSmActive: { backgroundColor: c.primary, borderColor: c.primary },
  chipSmText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium, textTransform: 'capitalize' },
  locAssignRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  addLocRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: spacing.xs },
  deleteBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: spacing.md, marginTop: spacing.sm },
  deleteText: { fontSize: fontSize.sm, color: c.danger, fontWeight: fontWeight.medium },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
